import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  AlertTriangle, 
  Shield, 
  CheckCircle, 
  Clock, 
  MapPin, 
  Phone, 
  Heart,
  Home,
  Car,
  Briefcase,
  Users,
  ChevronDown,
  ChevronRight,
  Download,
  Star,
  Zap
} from 'lucide-react';
import { SAFETY_PROTOCOLS } from '../../data/emergencyData.ts';
import EmergencyFeaturesHub from './EmergencyFeaturesHub';
import EmergencyContactsModal from './EmergencyContactsModal';
import DownloadGuidesModal from './DownloadGuidesModal';
import EvacuationRoutesModal from './EvacuationRoutesModal';

const SafetyGuide = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedProtocol, setExpandedProtocol] = useState<string | null>(null);
  const [completedSteps, setCompletedSteps] = useState<Record<string, boolean[]>>({});
  const [isEmergencyFeaturesOpen, setIsEmergencyFeaturesOpen] = useState(false);
  const [activeModal, setActiveModal] = useState<string | null>(null);

  // Protocol type to GeoThreat category mapping
  const protocolTypeToGeoThreatCategory: Record<string, string> = {
    // Natural disasters
    'earthquake': 'natural_disaster',
    'wildfire': 'natural_disaster', 
    'flood': 'natural_disaster',
    'hurricane': 'natural_disaster',
    'tornado': 'natural_disaster',
    'fire': 'natural_disaster',
    
    // Human health
    'infectious_disease': 'human_health',
    'environmental_health': 'human_health',
    
    // War and conflict
    'civil_war': 'war_conflict',
    'terrorism': 'war_conflict',
    
    // Animal health
    'wildlife_disease': 'animal_health',
    'livestock_health': 'animal_health',
    
    // Plant and agriculture
    'crop_disease': 'plant_agriculture',
    'invasive_species': 'plant_agriculture',
    
    // Environmental
    'pollution': 'environmental',
    'climate_change': 'environmental',
    
    // Technological
    'cybersecurity': 'technological',
    'nuclear_threats': 'technological',
    'ai_risks': 'technological'
  };

  const categories = [
    { id: 'all', label: 'All Disasters', icon: Shield },
    { id: 'natural_disaster', label: 'Natural Disasters', icon: AlertTriangle },
    { id: 'human_health', label: 'Human Health', icon: AlertTriangle },
    { id: 'war_conflict', label: 'War & Conflict', icon: AlertTriangle },
    { id: 'animal_health', label: 'Animal Health', icon: AlertTriangle },
    { id: 'plant_agriculture', label: 'Plant & Agriculture', icon: AlertTriangle },
    { id: 'environmental', label: 'Environmental', icon: AlertTriangle },
    { id: 'technological', label: 'Technological', icon: AlertTriangle },
    { id: 'earthquake', label: 'Earthquakes', icon: AlertTriangle },
    { id: 'wildfire', label: 'Wildfires', icon: AlertTriangle },
    { id: 'flood', label: 'Floods', icon: AlertTriangle },
    { id: 'hurricane', label: 'Hurricanes', icon: AlertTriangle },
    { id: 'tornado', label: 'Tornadoes', icon: AlertTriangle },
    { id: 'fire', label: 'Fire Emergency', icon: AlertTriangle },
  ];

  const emergencyKits = [
    {
      category: 'Basic Emergency Kit',
      icon: Home,
      items: [
        'Water (1 gallon per person per day for 3 days)',
        'Non-perishable food (3-day supply)',
        'Battery-powered or hand crank radio',
        'Flashlight and extra batteries',
        'First aid kit',
        'Whistle for signaling help',
        'Dust masks and plastic sheeting',
        'Moist towelettes and garbage bags',
        'Wrench or pliers to turn off utilities',
        'Manual can opener',
        'Local maps',
        'Cell phone with chargers'
      ]
    },
    {
      category: 'Vehicle Emergency Kit',
      icon: Car,
      items: [
        'Jumper cables',
        'Fire extinguisher',
        'Emergency flares or reflectors',
        'Tire pressure gauge and pump',
        'Motor oil and other fluids',
        'Emergency blanket',
        'First aid supplies',
        'Non-perishable snacks',
        'Water bottles',
        'Multi-tool or knife',
        'Duct tape',
        'Rain gear and warm clothing'
      ]
    },
    {
      category: 'Workplace Emergency Kit',
      icon: Briefcase,
      items: [
        'Comfortable walking shoes',
        'Small flashlight and batteries',
        'Whistle',
        'Personal medications',
        'Emergency contact information',
        'Small amount of cash',
        'Emergency blanket',
        'Work gloves',
        'Dust masks',
        'Plastic bags',
        'Permanent marker',
        'Notepad and pencil'
      ]
    }
  ];

  const filteredProtocols = selectedCategory === 'all' 
    ? SAFETY_PROTOCOLS 
    : SAFETY_PROTOCOLS.filter(protocol => {
        // Check if it's a direct type match
        if (protocol.type === selectedCategory) {
          return true;
        }
        // Check if it matches a GeoThreat category
        const protocolCategory = protocolTypeToGeoThreatCategory[protocol.type];
        return protocolCategory === selectedCategory;
      });

  const toggleStep = (protocolId: string, stepIndex: number) => {
    setCompletedSteps(prev => ({
      ...prev,
      [protocolId]: {
        ...prev[protocolId],
        [stepIndex]: !prev[protocolId]?.[stepIndex]
      }
    }));
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-accent-red border-accent-red/30 bg-accent-red/10';
      case 'medium': return 'text-accent-orange border-accent-orange/30 bg-accent-orange/10';
      case 'low': return 'text-accent-green border-accent-green/30 bg-accent-green/10';
      default: return 'text-gray-400 border-gray-600/30 bg-gray-600/10';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pt-20 pb-8 px-4"
    >
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-primary-400 to-primary-500 bg-clip-text text-transparent">
            Safety Guide
          </h1>
          <p className="text-gray-300">
            Essential protocols, emergency kits, and safety procedures to protect yourself and your loved ones during disasters.
          </p>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid md:grid-cols-4 gap-4 mb-8"
        >
          <button
            onClick={() => setActiveModal('emergency-contacts')}
            className="bg-dark-800/50 backdrop-blur-sm border border-dark-700 rounded-xl p-4 hover:border-primary-400/50 transition-all cursor-pointer"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-accent-red/20 rounded-lg flex items-center justify-center">
                <Phone className="w-5 h-5 text-accent-red" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Emergency Contacts</h3>
                <p className="text-sm text-gray-400">911 • Local Emergency Services</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setActiveModal('download-guides')}
            className="bg-dark-800/50 backdrop-blur-sm border border-dark-700 rounded-xl p-4 hover:border-primary-400/50 transition-all cursor-pointer"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary-400/20 rounded-lg flex items-center justify-center">
                <Download className="w-5 h-5 text-primary-400" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Download Guides</h3>
                <p className="text-sm text-gray-400">Offline safety protocols</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setActiveModal('evacuation-routes')}
            className="bg-dark-800/50 backdrop-blur-sm border border-dark-700 rounded-xl p-4 hover:border-primary-400/50 transition-all cursor-pointer"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-accent-green/20 rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-accent-green" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Evacuation Routes</h3>
                <p className="text-sm text-gray-400">Find nearest safe zones</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => setIsEmergencyFeaturesOpen(true)}
            className="bg-dark-800/50 backdrop-blur-sm border border-dark-700 rounded-xl p-4 hover:border-accent-orange/50 transition-all cursor-pointer"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-accent-orange/20 rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-accent-orange" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Emergency Hub</h3>
                <p className="text-sm text-gray-400">All emergency features</p>
              </div>
            </div>
          </button>
        </motion.div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Category Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-1"
          >
            <div className="bg-dark-800/50 backdrop-blur-sm border border-dark-700 rounded-xl p-6 sticky top-24">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary-400" />
                Categories
              </h3>
              <div className="space-y-2">
                {categories.map((category) => {
                  const Icon = category.icon;
                  return (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-all ${
                        selectedCategory === category.id
                          ? 'bg-primary-400/20 text-primary-400 border border-primary-400/30'
                          : 'text-gray-300 hover:bg-dark-700/50 hover:text-white'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-sm">{category.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-8">
            {/* Safety Protocols */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-accent-orange" />
                Safety Protocols
              </h2>
              
              <div className="space-y-4">
                {filteredProtocols.map((protocol, index) => (
                  <motion.div
                    key={protocol.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`border rounded-xl overflow-hidden transition-all duration-300 ${getPriorityColor(protocol.priority)}`}
                  >
                    <div
                      className="p-6 cursor-pointer"
                      onClick={() => setExpandedProtocol(
                        expandedProtocol === protocol.id ? null : protocol.id
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-xl font-semibold text-white capitalize">
                              {protocol.title}
                            </h3>
                            <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${getPriorityColor(protocol.priority)}`}>
                              {protocol.priority}
                            </span>
                          </div>
                          <p className="text-gray-300 mb-3">{protocol.description}</p>
                          <div className="flex items-center space-x-4 text-sm text-gray-400">
                            <div className="flex items-center space-x-1">
                              <Clock className="w-4 h-4" />
                              <span>{protocol.estimatedTime}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Users className="w-4 h-4" />
                              <span>{protocol.steps.length} steps</span>
                            </div>
                          </div>
                        </div>
                        <div className="ml-4">
                          {expandedProtocol === protocol.id ? (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Steps */}
                    {expandedProtocol === protocol.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="border-t border-current/20"
                      >
                        <div className="p-6">
                          <h4 className="font-semibold mb-4 text-white">Step-by-Step Instructions:</h4>
                          <div className="space-y-3">
                            {protocol.steps.map((step, stepIndex) => (
                              <motion.div
                                key={stepIndex}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: stepIndex * 0.1 }}
                                className="flex items-start space-x-3"
                              >
                                <button
                                  onClick={() => toggleStep(protocol.id, stepIndex)}
                                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mt-0.5 transition-all ${
                                    completedSteps[protocol.id]?.[stepIndex]
                                      ? 'bg-accent-green border-accent-green'
                                      : 'border-gray-500 hover:border-accent-green'
                                  }`}
                                >
                                  {completedSteps[protocol.id]?.[stepIndex] && (
                                    <CheckCircle className="w-4 h-4 text-white" />
                                  )}
                                </button>
                                <div className="flex-1">
                                  <span className="text-sm font-medium text-primary-400 mr-2">
                                    Step {stepIndex + 1}:
                                  </span>
                                  <span className={`text-gray-300 ${
                                    completedSteps[protocol.id]?.[stepIndex] ? 'line-through opacity-60' : ''
                                  }`}>
                                    {step}
                                  </span>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Emergency Kits */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Heart className="w-6 h-6 text-accent-red" />
                Emergency Preparedness Kits
              </h2>
              
              <div className="grid md:grid-cols-1 gap-6">
                {emergencyKits.map((kit, index) => {
                  const Icon = kit.icon;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-dark-800/50 backdrop-blur-sm border border-dark-700 rounded-xl p-6 hover:border-dark-600 transition-all"
                    >
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-primary-400 to-primary-500 rounded-lg flex items-center justify-center">
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-xl font-semibold text-white">{kit.category}</h3>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-2">
                        {kit.items.map((item, itemIndex) => (
                          <motion.div
                            key={itemIndex}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: itemIndex * 0.05 }}
                            className="flex items-center space-x-2 text-gray-300 text-sm py-1"
                          >
                            <div className="w-1.5 h-1.5 bg-primary-400 rounded-full flex-shrink-0"></div>
                            <span>{item}</span>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>

            {/* Important Tips */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-gradient-to-r from-accent-orange/10 to-accent-red/10 border border-accent-orange/30 rounded-xl p-6"
            >
              <div className="flex items-center space-x-3 mb-4">
                <Star className="w-6 h-6 text-accent-orange" />
                <h3 className="text-xl font-semibold text-white">Important Safety Tips</h3>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 text-gray-300">
                <div className="space-y-2">
                  <p className="flex items-start space-x-2">
                    <span className="text-accent-orange mt-1">•</span>
                    <span>Always have multiple communication methods available</span>
                  </p>
                  <p className="flex items-start space-x-2">
                    <span className="text-accent-orange mt-1">•</span>
                    <span>Keep important documents in waterproof containers</span>
                  </p>
                  <p className="flex items-start space-x-2">
                    <span className="text-accent-orange mt-1">•</span>
                    <span>Practice evacuation routes with your family regularly</span>
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="flex items-start space-x-2">
                    <span className="text-accent-orange mt-1">•</span>
                    <span>Stay informed through official emergency channels</span>
                  </p>
                  <p className="flex items-start space-x-2">
                    <span className="text-accent-orange mt-1">•</span>
                    <span>Never ignore evacuation orders from authorities</span>
                  </p>
                  <p className="flex items-start space-x-2">
                    <span className="text-accent-orange mt-1">•</span>
                    <span>Check and update emergency supplies every 6 months</span>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Emergency Features Hub */}
      <EmergencyFeaturesHub
        isOpen={isEmergencyFeaturesOpen}
        onClose={() => setIsEmergencyFeaturesOpen(false)}
      />

      {/* Conditional Modal Rendering */}
      {activeModal === 'emergency-contacts' && (
        <EmergencyContactsModal onClose={() => setActiveModal(null)} />
      )}
      
      {activeModal === 'download-guides' && (
        <DownloadGuidesModal onClose={() => setActiveModal(null)} />
      )}
      
      {activeModal === 'evacuation-routes' && (
        <EvacuationRoutesModal onClose={() => setActiveModal(null)} />
      )}
    </motion.div>
  );
};

export default SafetyGuide;